import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { gamificationAPI, resolveFileUrl } from '../services/api';
import type { LeaderboardEntry } from '../types';

const PURPLE = '#200854';
const ORANGE = '#FF6600';

const LEVEL_COLORS: Record<string, string> = {
  Rider: '#9CA3AF',
  Grinder: '#F59E0B',
  Pacer: '#3B82F6',
  Dominator: '#EF4444',
};

interface StaffLeaderboardEntry {
  rank: number;
  userId: string;
  name: string | null;
  surname: string | null;
  role: string;
  rideCount: number;
  comp1st: number;
  comp2nd: number;
  comp3rd: number;
  boarPoints: number;
  photoUrl: string | null;
}

const BOAR_MEDALS = ['🐗', '🐖', '🐽'];

const NajSportLeaderboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [staffBoard, setStaffBoard] = useState<StaffLeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const stored = localStorage.getItem('najsportUser');
  const currentUser = stored ? JSON.parse(stored) : null;
  const isStaff = currentUser && ['TRAINER', 'ADMIN', 'POWER_ADMIN'].includes(currentUser.role);

  useEffect(() => {
    if (!currentUser) { navigate('/'); return; }
    const tasks: Promise<unknown>[] = [
      gamificationAPI.getLeaderboard(50).then(setLeaderboard).catch(() => {}),
    ];
    if (isStaff) {
      tasks.push(gamificationAPI.getStaffLeaderboard().then(setStaffBoard).catch(() => {}));
    }
    Promise.all(tasks).finally(() => setLoading(false));
  }, []);

  if (!currentUser) return null;

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="shadow-md" style={{ backgroundColor: PURPLE }}>
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/najsport-logo.png" alt="NajSport" className="h-8 w-auto" />
            <h1 className="text-lg font-bold text-white">{t('najsport.leaderboard')}</h1>
          </div>
          <button
            onClick={() => navigate('/events')}
            className="flex items-center gap-1 text-sm text-white hover:text-orange-300 transition-colors font-medium"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            {t('najsport.rides')}
          </button>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6">
        {isStaff && staffBoard.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-2xl">🐗</span>
              <h2 className="text-base font-bold" style={{ color: PURPLE }}>{t('najsport.boarLeague')}</h2>
              <span className="text-[10px] text-gray-400 uppercase tracking-wide">{t('najsport.staffOnly')}</span>
            </div>
            <div className="space-y-2">
              {staffBoard.map((entry) => {
                const isMe = entry.userId === currentUser?.id;
                const initials = ((entry.name?.[0] || '') + (entry.surname?.[0] || '')).toUpperCase() || '?';
                const medal = entry.rank <= 3 ? BOAR_MEDALS[entry.rank - 1] : null;
                return (
                  <div
                    key={entry.userId}
                    className={`bg-gradient-to-r from-amber-50 to-white rounded-xl p-3 flex items-center gap-3 border border-amber-200 ${isMe ? 'ring-2' : ''}`}
                    style={isMe ? { boxShadow: `0 0 0 2px ${ORANGE}` } : {}}
                  >
                    <div className="w-8 text-center font-bold text-sm" style={{ color: PURPLE }}>
                      {medal || `${entry.rank}.`}
                    </div>
                    {entry.photoUrl ? (
                      <img src={resolveFileUrl(entry.photoUrl)} alt="" className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold"
                        style={{ backgroundColor: PURPLE }}
                      >
                        {initials}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-semibold truncate ${isMe ? 'text-orange-600' : 'text-gray-900'}`}>
                        {entry.name || ''} {entry.surname || ''}
                      </div>
                      <div className="text-[10px] text-gray-500 mt-0.5 flex items-center gap-1.5">
                        <span>🐽 {entry.rideCount}</span>
                        {entry.comp1st > 0 && <span>🥇 {entry.comp1st}</span>}
                        {entry.comp2nd > 0 && <span>🥈 {entry.comp2nd}</span>}
                        {entry.comp3rd > 0 && <span>🥉 {entry.comp3rd}</span>}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold" style={{ color: PURPLE }}>
                        {entry.boarPoints.toLocaleString('sk-SK')}
                      </div>
                      <div className="text-[10px] text-gray-400">{t('najsport.boarPointsUnit')}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 border-t border-dashed border-amber-300" />
          </div>
        )}

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="bg-white rounded-xl p-4 animate-pulse">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gray-200" />
                  <div className="flex-1"><div className="h-4 bg-gray-200 rounded w-32" /></div>
                  <div className="h-6 bg-gray-200 rounded w-12" />
                </div>
              </div>
            ))}
          </div>
        ) : leaderboard.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <div className="text-4xl mb-2">🏆</div>
            <div className="text-sm">{t('najsport.noPointsYet')}</div>
          </div>
        ) : (
          <div className="space-y-2">
            {/* Top 3 podium */}
            {leaderboard.length >= 3 && (
              <div className="flex items-end justify-center gap-3 mb-6 pt-4">
                {[1, 0, 2].map(idx => {
                  const entry = leaderboard[idx];
                  if (!entry) return null;
                  const isFirst = idx === 0;
                  const medal = ['🥇', '🥈', '🥉'][entry.rank - 1];
                  const initials = ((entry.name?.[0] || '') + (entry.surname?.[0] || '')).toUpperCase() || '?';
                  const isMe = entry.userId === currentUser?.id;
                  return (
                    <div key={entry.userId} className={`flex flex-col items-center ${isFirst ? 'order-2' : idx === 1 ? 'order-1' : 'order-3'}`}>
                      <div className="text-2xl mb-1">{medal}</div>
                      <div
                        className={`rounded-full flex items-center justify-center text-white font-bold border-3 ${isFirst ? 'w-16 h-16 text-lg' : 'w-12 h-12 text-sm'}`}
                        style={{
                          backgroundColor: PURPLE,
                          borderColor: isMe ? ORANGE : 'transparent',
                          borderWidth: isMe ? 3 : 0,
                        }}
                      >
                        {entry.photoUrl ? (
                          <img src={resolveFileUrl(entry.photoUrl)} alt="" className="w-full h-full rounded-full object-cover" />
                        ) : initials}
                      </div>
                      <div className={`text-xs font-bold mt-1 ${isMe ? 'text-orange-600' : ''}`} style={{ color: isMe ? ORANGE : PURPLE }}>
                        {entry.name || '?'}
                      </div>
                      <div className="text-lg font-bold" style={{ color: PURPLE }}>{entry.totalPoints}</div>
                      <span
                        className="text-[10px] font-bold px-1.5 py-0.5 rounded-full text-white"
                        style={{ backgroundColor: LEVEL_COLORS[entry.level.name] || PURPLE }}
                      >
                        {entry.level.name}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Full list */}
            {leaderboard.map((entry) => {
              const isMe = entry.userId === currentUser?.id;
              const initials = ((entry.name?.[0] || '') + (entry.surname?.[0] || '')).toUpperCase() || '?';
              return (
                <div
                  key={entry.userId}
                  className={`bg-white rounded-xl p-3 flex items-center gap-3 ${isMe ? 'ring-2' : ''}`}
                  style={isMe ? { boxShadow: `0 0 0 2px ${ORANGE}` } : {}}
                >
                  {/* Rank */}
                  <div className="w-8 text-center font-bold text-sm" style={{ color: PURPLE }}>
                    {entry.rank <= 3 ? ['🥇', '🥈', '🥉'][entry.rank - 1] : `${entry.rank}.`}
                  </div>

                  {/* Avatar */}
                  {entry.photoUrl ? (
                    <img src={resolveFileUrl(entry.photoUrl)} alt="" className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold"
                      style={{ backgroundColor: PURPLE }}
                    >
                      {initials}
                    </div>
                  )}

                  {/* Name & Level */}
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-semibold truncate ${isMe ? 'text-orange-600' : 'text-gray-900'}`}>
                      {entry.name || ''} {entry.surname || ''}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span
                        className="text-[10px] font-bold px-1.5 py-0.5 rounded-full text-white"
                        style={{ backgroundColor: LEVEL_COLORS[entry.level.name] || PURPLE }}
                      >
                        Lv.{entry.level.level} {entry.level.name}
                      </span>
                      {entry.currentStreak >= 3 && (
                        <span className="text-[10px] text-orange-500 font-bold">🔥 {entry.currentStreak}</span>
                      )}
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="text-right">
                    <div className="text-lg font-bold" style={{ color: PURPLE }}>{entry.totalPoints}</div>
                    <div className="text-[10px] text-gray-400">{entry.rideCount} {t('najsport.ridesCount').toLowerCase()}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

      </main>
    </div>
  );
};

export default NajSportLeaderboard;
